import { computed, defineComponent, watch, ref, onMounted, } from '@vue/composition-api';
import { getUserSurveysList, } from '@/api/v2/requests/surveys';
import { genericTableRowSize } from '@/components/Table/__new__/_components/TableCellGeneric';
import TableLoader from '@/views/_components/Table/TableLoader.vue';
import { GenericAGTable } from '@/components/Table/AGTable/AGTable.vue';
import FloatingTextFilter from '@/components/Table/AGTable/_components/FloatingTextFilter.vue';
import FloatingSelectFilter from '@/components/Table/AGTable/_components/FloatingSelectFilter.vue';
import FloatingDatepickerFiler from '@/components/Table/AGTable/_components/FloatingDatepickerFilter.vue';
import SurveyStatusBadge from '@/views/Brand/Surveys/_components/SurveyStatusBadge.vue';
import SurveyTitleCell from '@/views/Brand/Surveys/_components/SurveyTitleCell.vue';
import TableFooter from '@/views/_components/Table/TableFooter.vue';
import { useTableQueryParams } from '@/components/Table/_composables/use-table-query-params';
import { usePlaceManagerSurveyColumns } from '../_composables/use-place-manager-survey-columns';
import { SurveyStatus } from '@/api/v2/requests/surveys';
import SurveyModal from '@/layouts/_components/Surveys/SurveyModal.vue';
export default defineComponent({
    components: {
        TableFooter,
        TableLoader,
        SurveyModal,
        // eslint-disable-next-line vue/no-unused-components
        FloatingTextFilter,
        // eslint-disable-next-line vue/no-unused-components
        FloatingSelectFilter,
        // eslint-disable-next-line vue/no-unused-components
        FloatingDatepickerFiler,
        // eslint-disable-next-line vue/no-unused-components
        SurveyStatusBadge,
        // eslint-disable-next-line vue/no-unused-components
        SurveyTitleCell,
        AgTable: GenericAGTable(),
    },
    setup(props, ctx) {
        const isLoading = ref(false);
        const rows = ref([]);
        const totalCount = ref(0);
        const agTableRef = ref(null);
        const showSurveyModal = ref(false);
        const selectedSurvey = ref(null);
        const selectedPlace = ref(null);
        const isLoadingSurvey = ref(false);
        const isRefreshingFromSelf = ref(false);
        const { page, perPage, query: tableQuery, handleFilterChange: handleQueryFilterChange, handleSort, resetSort, } = useTableQueryParams(ctx, {
            defaultPerPage: 25,
            filterParams: {
                title: 'title',
                internalNumber: 'internalNumber',
                createdAt: 'createdAt',
            },
        });
        const query = computed(() => {
            return {
                ...tableQuery.value,
            };
        });
        const fetchItems = async () => {
            isLoading.value = true;
            try {
                const { nodes, totalCount: nodesCount } = await getUserSurveysList(query.value ?? {});
                totalCount.value = nodesCount;
                rows.value = nodes.map((mySurvey) => ({
                    id: mySurvey.id,
                    surveyId: mySurvey.survey.id,
                    title: mySurvey.survey.title,
                    internalNumber: mySurvey.survey.internalNumber,
                    createdAt: mySurvey.survey.createdAt,
                    updatedAt: mySurvey.survey.updatedAt,
                    publishedAt: mySurvey.publishedAt,
                    respondedAt: mySurvey.respondedAt,
                    aggregatedStatus: mySurvey.aggregatedStatus,
                    place: mySurvey.place,
                }));
            }
            catch (err) {
                ctx.root.$toast.error(ctx.root.$t(`brand-surveys.error-fetching-surveys`));
            }
            finally {
                isLoading.value = false;
            }
        };
        const changePage = (newPage) => {
            page.value = newPage;
        };
        const handleFilterChange = (filterModel) => {
            handleQueryFilterChange(filterModel);
        };
        const handleSortChange = (field, sort) => {
            handleSort(field, sort);
        };
        const handleResetSort = () => {
            resetSort();
        };
        const handlePerPageChange = (newPerPage) => {
            perPage.value = newPerPage;
            page.value = 1;
        };
        const handleRowClick = async (row) => {
            if (row.aggregatedStatus === SurveyStatus.WAITING) {
                await openSurveyModal(row);
            }
            else {
                if (!row.place?.id) {
                    ctx.root.$toast.error('Brak przypisanej placówki do ankiety');
                    return;
                }
                ctx.root.$router.push({
                    name: 'PlaceManagerSurvey',
                    params: {
                        surveyId: row.surveyId,
                    },
                    query: {
                        placeId: row.place.id,
                    },
                });
            }
        };
        const openSurveyModal = async (row) => {
            isLoadingSurvey.value = true;
            try {
                const { nodes } = await getUserSurveysList({
                    surveyId: row.surveyId,
                    placeId: row.place?.id,
                    perPage: 1,
                    page: 1,
                });
                if (nodes.length > 0) {
                    const mySurvey = nodes[0];
                    selectedSurvey.value = mySurvey.survey;
                    selectedPlace.value = mySurvey.place;
                    showSurveyModal.value = true;
                }
                else {
                    ctx.root.$toast.error(ctx.root.$t('brand-surveys.error-fetching-survey'));
                }
            }
            catch (error) {
                ctx.root.$toast.error(ctx.root.$t('brand-surveys.error-fetching-survey'));
            }
            finally {
                isLoadingSurvey.value = false;
            }
        };
        const closeSurveyModal = () => {
            showSurveyModal.value = false;
            selectedSurvey.value = null;
            selectedPlace.value = null;
        };
        const refreshTableUntilUpdated = async () => {
            const initialWaitingCount = rows.value.filter((row) => row.aggregatedStatus === SurveyStatus.WAITING).length;
            for (let attempt = 1; attempt <= 5; attempt++) {
                await fetchItems();
                const currentWaitingCount = rows.value.filter((row) => row.aggregatedStatus === SurveyStatus.WAITING).length;
                if (currentWaitingCount < initialWaitingCount) {
                    return;
                }
                if (attempt < 5) {
                    await new Promise((resolve) => setTimeout(resolve, 300));
                }
            }
        };
        const handleSurveySuccess = () => {
            closeSurveyModal();
            fetchItems();
            isRefreshingFromSelf.value = true;
            ctx.root.$store.dispatch('ui/triggerSurveyRefresh');
        };
        onMounted(async () => {
            await fetchItems();
        });
        watch(() => ctx.root.$store.getters['ui/surveyRefreshTrigger'], () => {
            if (isRefreshingFromSelf.value) {
                isRefreshingFromSelf.value = false;
                return;
            }
            refreshTableUntilUpdated();
        });
        const { columns } = usePlaceManagerSurveyColumns(ctx);
        watch(query, () => {
            fetchItems();
        }, { deep: true });
        return {
            columns,
            tableQuery,
            page,
            perPage,
            totalCount,
            genericTableRowSize,
            rows,
            isLoading,
            handleFilterChange,
            handleSortChange,
            handleResetSort,
            changePage,
            handlePerPageChange,
            agTableRef,
            handleRowClick,
            showSurveyModal,
            selectedSurvey,
            selectedPlace,
            isLoadingSurvey,
            closeSurveyModal,
            handleSurveySuccess,
        };
    },
});
